import { useState } from 'react';
import { formatDateTime } from '@/lib/dateFormat';
import { useStatuses, useTeamHistory, useTeamDepartments } from '@/hooks/useAttendance';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateRangePresetPicker, useFilterState } from '@/components/filters';
import { StatusSelect } from '@/components/status/StatusSelect';
import { TablePagination } from '@/components/ui/TablePagination';
import { OverbreakBadge, calcOverbreakSeconds } from '@/components/attendance/OverbreakBadge';
import { getMultiStaffAttendanceEndDisplay } from '@/lib/attendanceEndStatus';
import { safeArray } from '@/lib/safeArray';
import { StaffPickerButton, type SelectedStaff } from '@/components/staff/StaffPickerButton';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds % 60}s`;
}

// Staff object persisted for display; only `id` goes to the API
type TeamHistoryFilter = {
  startDate: string;
  endDate: string;
  statusId: string;
  selectedStaff: SelectedStaff | null;
  departmentId: string;
} & Record<string, unknown>;
const DEFAULT_FILTER: TeamHistoryFilter = { startDate: '', endDate: '', statusId: '', selectedStaff: null, departmentId: '' };

export function TeamHistoryPage() {
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [filter, setFilter, resetFilter] = useFilterState<TeamHistoryFilter>({
    key: 'vibe365.team-history.filter',
    defaultValue: DEFAULT_FILTER,
    mode: 'localStorage',
  });

  const { data: statusesData = [] } = useStatuses();
  const { data: departmentsData = [] } = useTeamDepartments();
  const { data, isLoading } = useTeamHistory({
    page,
    limit: 20,
    staffId: filter.selectedStaff?.id || undefined,
    departmentId: filter.departmentId || undefined,
    startDate: filter.startDate || undefined,
    endDate: filter.endDate || undefined,
    statusId: filter.statusId || undefined,
    clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    sortBy,
    sortDir,
  });

  const records = safeArray(data?.data);
  const pagination = data?.pagination;

  function toggleSort(col: string) {
    setSortBy((prev) => {
      if (prev !== col) { setSortDir('desc'); return col; }
      setSortDir((d) => d === 'desc' ? 'asc' : 'desc');
      return col;
    });
    setPage(1);
  }

  function SortIcon({ col }: { col: string }) {
    if (sortBy !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-1" />
      : <ChevronDown className="inline h-3 w-3 ml-1" />;
  }

  function updateFilter(patch: Partial<TeamHistoryFilter>) {
    setFilter((f) => ({ ...f, ...patch }));
    setPage(1);
  }

  const handleReset = () => {
    resetFilter();
    setPage(1);
    setSortBy(undefined);
    setSortDir('desc');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Team Attendance</h1>
        <p className="text-sm text-muted-foreground">View your team's attendance records</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-3 rounded-xl border bg-card p-4 shadow-sm">
        <StaffPickerButton
          value={filter.selectedStaff}
          onSelect={(s) => updateFilter({ selectedStaff: s })}
          label="Staff"
          placeholder="All staff"
        />
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Department</span>
          <Select
            value={filter.departmentId || '__all__'}
            onValueChange={(v) => updateFilter({ departmentId: v === '__all__' ? '' : v })}
          >
            <SelectTrigger className="h-10 w-full sm:w-[16rem]">
              <SelectValue placeholder="All departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All departments</SelectItem>
              {departmentsData.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DateRangePresetPicker
          value={{ startDate: filter.startDate, endDate: filter.endDate }}
          onChange={({ startDate: nextStartDate, endDate: nextEndDate }) =>
            updateFilter({ startDate: nextStartDate ?? '', endDate: nextEndDate ?? '' })
          }
          className="w-full sm:w-[20rem]"
        />
        <StatusSelect
          label="Status"
          value={filter.statusId}
          options={statusesData}
          placeholder="Choose a status to filter"
          emptyLabel="All statuses"
          onValueChange={(nextValue) => updateFilter({ statusId: nextValue })}
          className="w-full sm:w-[18rem]"
          triggerClassName="w-full"
          testId="team-history-status-filter"
        />
        <div className="flex flex-col gap-1">
          <span className="text-xs invisible select-none" aria-hidden="true">Reset</span>
          <Button variant="outline" onClick={handleReset} className="h-10 w-full sm:w-auto">Reset</Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border bg-card shadow-sm">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : records.length === 0 ? (
            <div className="flex h-32 items-center justify-center">
              <p className="text-sm text-muted-foreground">No records found</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left text-xs text-muted-foreground">
                  <th className="px-3 py-3 font-medium cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('staff')}>Staff<SortIcon col="staff" /></th>
                  <th className="px-3 py-3 font-medium cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('department')}>Department<SortIcon col="department" /></th>
                  <th className="px-3 py-3 font-medium cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('status')}>Status<SortIcon col="status" /></th>
                  <th className="px-3 py-3 font-medium cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('start')}>Start<SortIcon col="start" /></th>
                  <th className="px-3 py-3 font-medium cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('end')}>End<SortIcon col="end" /></th>
                  <th className="px-3 py-3 font-medium cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('duration')}>Duration<SortIcon col="duration" /></th>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Overbreak</th>
                  <th className="px-3 py-3 font-medium cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('notes')}>Notes<SortIcon col="notes" /></th>
                  <th className="px-3 py-3 font-medium cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('createdAt')}>Created At<SortIcon col="createdAt" /></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {records.map((record, index) => {
                  const endDisplay = getMultiStaffAttendanceEndDisplay(records, index, (item) => item.staffId);

                  return <tr key={record.id} className="hover:bg-muted/30">
                    <td className="px-3 py-3">
                      <div>
                        <p className="font-medium">
                          {record.staff?.firstName} {record.staff?.surname}
                        </p>
                        <p className="text-xs text-muted-foreground">{record.staff?.employeeId ?? '—'}</p>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-sm">
                      {record.staff?.department?.name ?? '—'}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: record.status.colorHex ?? '#6b7280' }}
                        />
                        {record.status.displayName ?? record.status.name}
                      </div>
                    </td>
                    <td className="px-3 py-3 font-mono text-xs">
                      {formatDateTime(record.startTime)}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs">
                      {record.endTime
                        ? formatDateTime(record.endTime)
                        : <span className={endDisplay?.className}>{endDisplay?.label}</span>}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs">
                      {formatDuration(record.durationSeconds)}
                    </td>
                    <td className="px-3 py-3">
                      <OverbreakBadge
                        overbreakSeconds={calcOverbreakSeconds(
                          record.durationSeconds,
                          record.status.isBreak,
                          record.status.maxDurationSeconds,
                        )}
                      />
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">
                      {record.notes ?? '—'}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-muted-foreground">
                      {formatDateTime(record.logCreatedAt)}
                    </td>
                  </tr>;
                })}
              </tbody>
            </table>
          )}
        </div>

        {pagination && (
          <TablePagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}
